import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Award, Handshake, Star, ChevronDown, Gauge, Play, Car, Clock, Flag } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";
import VideoModal from "@/components/VideoModal";
import SEO from "@/components/SEO";
import { toStreamableVideoUrl, getYouTubeId } from "@/lib/videoUrl";

// Porsche 911 rouge (992 S Cabriolet Rouge Guards) — hero d'accueil.
// L'admin peut uploader une vidéo à la place via /admin/site (champ "Vidéo hero page ACCUEIL").
const HERO_BG = "https://d2j6dbq0eux0bg.cloudfront.net/images/113000503/products/783935758/5194593295.jpg";
const SIM_BG = "https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&w=1900&q=80";
const BRAND_LIST = ["Porsche", "BMW", "Land Rover", "Alpine", "Nissan GT-R", "Alfa Romeo", "Porsche Classic", "Youngtimers", "Oldtimers"];

export default function Home() {
  const { t, pick } = useLang();
  const [site, setSite] = useState({});
  const [featured, setFeatured] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [videoOpen, setVideoOpen] = useState(false);
  const [stats, setStats] = useState({ available: 0, reserved: 0, sold: 0 });
  const heroYtId = getYouTubeId(site.hero_video_url);

  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
    api.get("/vehicles", { params: { is_featured: true, limit: 6 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/vehicles", { params: { limit: 500 } }).then((r) => {
      const vs = r.data || [];
      setStats({
        available: vs.filter((v) => v.status === "available").length,
        reserved: vs.filter((v) => v.status === "reserved").length,
        sold: vs.filter((v) => v.status === "sold").length,
      });
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        fullTitle
        title="KV International — Achat et Vente de Porsche de prestige en Belgique · Dépôt-vente d'exception · Rhode-Saint-Genèse"
        description="Achat et vente de Porsche de prestige en Belgique. Dépôt-vente de véhicules d'exception, achat cash 24-48 h, recherche personnalisée et coaching pilotage sur simulateur pro. KV International — Rhode-Saint-Genèse, sur rendez-vous."
      />
      {/* HERO */}
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <div className="absolute inset-0">
          {heroYtId ? (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${heroYtId}?autoplay=1&mute=1&loop=1&playlist=${heroYtId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3`}
                title="Porsche 911 — Spécialiste Porsche KV International, Rhode-Saint-Genèse"
                allow="autoplay; encrypted-media"
                data-testid="home-hero-video"
                className="absolute top-1/2 left-1/2 w-[max(100%,177.78vh)] h-[max(100%,56.25vw)] -translate-x-1/2 -translate-y-1/2 border-0"
              />
            </div>
          ) : site.hero_video_url ? (
            <video key={site.hero_video_url} autoPlay muted loop playsInline preload="metadata" poster={HERO_BG} className="w-full h-full object-cover img-fade" data-testid="home-hero-video" aria-label="Porsche 911 — Spécialiste Porsche KV International, Rhode-Saint-Genèse">
              <source src={toStreamableVideoUrl(site.hero_video_url)} type="video/mp4" />
            </video>
          ) : (
            <img src={HERO_BG} alt="Porsche 911 Carrera Cabriolet rouge dans le showroom KV International à Rhode-Saint-Genèse — voitures de luxe et occasion prestige Belgique" fetchpriority="high" decoding="async" className="w-full h-full object-cover kenburns img-fade" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/50 to-[#0D0D0D]/70" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-24 pt-32 w-full">
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#C8A75A] fade-up">Showroom · Chaussée de Waterloo 242 · 1640 Rhode-Saint-Genèse · Sur rendez-vous</p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight font-light text-white mt-6 max-w-5xl fade-up fade-up-delay-1 leading-[0.95]">
            {pick(site.hero_title) || "KV International — Spécialiste Porsche"}
          </h1>
          <p className="text-xl md:text-2xl text-white/70 mt-8 max-w-2xl leading-relaxed fade-up fade-up-delay-2 font-light">
            {pick(site.hero_subtitle)}
          </p>

          <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-12 fade-up fade-up-delay-3">
            <Link to="/stock" data-testid="hero-cta-stock" className="group relative bg-[#C8A75A] text-[#0D0D0D] hover:bg-white transition-colors duration-300 font-semibold px-10 py-5 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-3 shadow-[0_8px_32px_-8px_rgba(200,167,90,0.6)] ring-1 ring-[#C8A75A]">
              <span>Découvrir notre stock</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-6 mt-6 text-xs uppercase tracking-[0.25em] text-white/50 fade-up fade-up-delay-4">
            <Link to="/simulator#reserver" data-testid="hero-cta-simulator" className="hover:text-[#C8A75A] transition-colors inline-flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5" /> Simulateur pro
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/sell" data-testid="hero-cta-sell" className="hover:text-[#C8A75A] transition-colors">{t.hero.sell}</Link>
          </div>
        </div>

        <div className="relative z-10 flex justify-center pb-8 text-white/40 fade-up fade-up-delay-4">
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </div>
      </section>

      {/* WHY */}
      <section className="py-32 lg:py-44 bg-[#0D0D0D]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start mb-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.home.why_kicker}</p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white">{t.home.why_title}</h2>
            </div>
            <p className="text-white/60 text-base md:text-lg leading-relaxed lg:pt-4">
              {pick(site.about_text)}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: t.home.why_1_title, text: t.home.why_1_text },
              { icon: Handshake, title: t.home.why_2_title, text: t.home.why_2_text },
              { icon: Truck, title: t.home.why_3_title, text: t.home.why_3_text },
              { icon: ShieldCheck, title: t.home.why_4_title, text: t.home.why_4_text },
            ].map((c, i) => (
              <div key={i} data-testid={`why-card-${i}`} className="bg-[#1E1E1E] border border-white/10 rounded-md p-8 hover:border-[#C8A75A]/40 transition-colors duration-500">
                <c.icon className="w-8 h-8 text-[#C8A75A] mb-6" strokeWidth={1.2} />
                <h3 className="font-display text-xl text-white mb-3">{c.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section data-testid="featured-section" className="py-32 lg:py-44 bg-[#1E1E1E]/30 border-y border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.home.featured_kicker}</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white">{t.home.featured_title}</h2>
              </div>
              <Link to="/stock" className="text-sm uppercase tracking-[0.2em] text-[#C8A75A] hover:text-white inline-flex items-center gap-2">
                {t.home.featured_view_all} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.slice(0, 6).map((v) => <VehicleCard key={v.id} vehicle={v} />)}
            </div>
          </div>
        </section>
      )}

      {/* SELL / BUY-BACK PROMO */}
      <section data-testid="home-sell-section" className="relative py-32 lg:py-44 overflow-hidden">
        {site.hero_video_url && false && (
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <video autoPlay muted loop playsInline className="w-full h-full object-cover">
              <source src={site.hero_video_url} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/70 to-transparent" />
          </div>
        )}
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">Rachat · Dépôt-vente · Showroom Rhode-Saint-Genèse</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-6">Vendre votre véhicule ?</h2>
            <p className="text-white/70 text-lg leading-relaxed max-w-xl mb-8">
              Deux formules — un seul interlocuteur. Nous rachetons votre voiture au juste prix,
              ou nous la mettons en dépôt-vente dans notre showroom. Aucune démarche administrative pour vous.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/sell" data-testid="home-sell-cta" className="bg-[#C8A75A] text-black hover:bg-white transition-colors duration-300 font-medium px-8 py-4 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
                Estimer mon véhicule <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/sell" className="border border-white/20 text-white hover:bg-white/5 transition-colors duration-300 px-8 py-4 rounded-md text-sm uppercase tracking-widest">
                Voir les 2 formules
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-[#1E1E1E] border border-white/10 rounded-md p-8 hover:border-[#C8A75A]/40 transition-colors">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A] mb-3">Solution 1</p>
              <h4 className="font-display text-2xl text-white mb-3">Rachat direct</h4>
              <p className="text-white/60 text-sm leading-relaxed">Offre ferme sous 24-48h. Paiement rapide. Zéro paperasse.</p>
            </div>
            <div className="bg-[#1E1E1E] border border-white/10 rounded-md p-8 hover:border-[#C8A75A]/40 transition-colors">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#C8A75A] mb-3">Solution 2</p>
              <h4 className="font-display text-2xl text-white mb-3">Dépôt-vente</h4>
              <p className="text-white/60 text-sm leading-relaxed">Vitrine premium & réseau ciblé. Prix optimisé pour vous.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SUR NOS ROUTES — bandeau parcours des autos */}
      <section data-testid="home-on-the-road-banner" className="bg-black border-y border-white/10 py-28 lg:py-36">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-8 mb-12">
            <div className="max-w-2xl">
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#C8A75A] mb-4">Le parcours de nos autos</p>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white leading-[1.05]">
                Découvrez le parcours de nos autos
              </h2>
              <p className="text-white/60 text-base leading-relaxed mt-5">
                De la vitrine du showroom à vos virages du week-end — chaque voiture qui passe chez KV International a son histoire. Voici les trois chapitres.
              </p>
            </div>
            <Link
              to="/sur-nos-routes"
              data-testid="home-otr-cta"
              className="inline-flex items-center gap-3 bg-[#C8A75A] text-black hover:bg-white transition-colors font-semibold px-7 py-4 rounded-md text-xs uppercase tracking-widest shadow-[0_8px_32px_-8px_rgba(200,167,90,0.5)]"
            >
              Voir toutes nos routes <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
            {[
              { key: "available", label: "À vendre", sub: "Disponibles maintenant", value: stats.available, icon: Car, tint: "text-emerald-400", ring: "border-emerald-500/30 hover:border-emerald-400/60", dot: "bg-emerald-400" },
              { key: "reserved", label: "Réservées", sub: "Sous option", value: stats.reserved, icon: Clock, tint: "text-amber-400", ring: "border-amber-500/25 hover:border-amber-400/50", dot: "bg-amber-400" },
              { key: "sold", label: "Trop tard", sub: "Déjà sur nos routes", value: stats.sold, icon: Flag, tint: "text-white/70", ring: "border-white/15 hover:border-white/30", dot: "bg-white/60" },
            ].map((s) => (
              <Link
                key={s.key}
                to={`/sur-nos-routes#${s.key}`}
                data-testid={`home-otr-stat-${s.key}`}
                className={`group bg-[#1E1E1E] border rounded-md p-8 lg:p-10 transition-colors ${s.ring}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] ${s.tint}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                    {s.label}
                  </span>
                  <s.icon className={`w-5 h-5 ${s.tint}`} strokeWidth={1.4} />
                </div>
                <p className="font-display text-6xl lg:text-7xl text-white leading-none tracking-tight">{s.value}</p>
                <div className="flex items-baseline justify-between mt-4">
                  <p className="text-white/50 text-sm">{s.sub}</p>
                  <span className="text-[10px] uppercase tracking-widest text-white/40 group-hover:text-[#C8A75A] transition-colors">Voir →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SIMULATOR PROMO */}
      <section data-testid="home-simulator-section" className="relative py-32 lg:py-44 overflow-hidden">
        <div className="absolute inset-0">
          <img src={SIM_BG} alt="Simulateur professionnel de conduite Porsche — KV International" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D] via-[#0D0D0D]/85 to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4 flex items-center gap-3">
              <Gauge className="w-4 h-4" /> {t.home.simulator_kicker}
            </p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-6">{t.home.simulator_title}</h2>
            <p className="text-white/70 text-lg leading-relaxed max-w-xl mb-8">{t.home.simulator_text}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/simulator" data-testid="home-sim-book" className="bg-[#C8A75A] text-black hover:bg-white transition-colors duration-300 font-medium px-8 py-4 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
                {t.home.simulator_cta} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/simulator" className="border border-white/20 text-white hover:bg-white/5 transition-colors duration-300 px-8 py-4 rounded-md text-sm uppercase tracking-widest">
                {t.nav.simulator}
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[t.simulator.block1_title, t.simulator.block2_title, t.simulator.block3_title].map((b, i) => (
              <div key={i} className="bg-[#1E1E1E]/80 backdrop-blur border border-white/10 rounded-md p-6">
                <span className="text-4xl font-display text-[#C8A75A]/40">0{i+1}</span>
                <p className="text-white text-sm mt-3 leading-snug">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section data-testid="testimonials-section" className="py-32 lg:py-44 bg-[#0D0D0D]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-4">{t.home.testimonials_kicker}</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-14">{t.home.testimonials_title}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((r) => (
                <div key={r.id} data-testid={`testimonial-${r.id}`} className="bg-[#1E1E1E] border border-white/10 rounded-md p-8">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: r.rating }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-[#C8A75A]" fill="#C8A75A" />
                    ))}
                  </div>
                  <p className="text-white/80 text-base leading-relaxed italic mb-6">« {pick(r.comment)} »</p>
                  <div className="pt-6 border-t border-white/10">
                    <p className="text-white font-medium">{r.name}</p>
                    {r.vehicle_bought && <p className="text-xs text-[#C8A75A] uppercase tracking-widest mt-1">{r.vehicle_bought}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BRANDS MARQUEE */}
      <section className="py-24 border-y border-white/10 bg-[#1E1E1E]/40 overflow-hidden">
        <p className="text-center text-xs uppercase tracking-[0.3em] text-[#C8A75A] mb-8">{t.home.brands_kicker}</p>
        <div className="marquee-track flex gap-16 whitespace-nowrap">
          {[...BRAND_LIST, ...BRAND_LIST].map((b, i) => (
            <span key={i} className="font-display text-2xl md:text-3xl text-white/30 tracking-wider">{b}</span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 lg:py-44 bg-[#0D0D0D]">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-white mb-6">{t.home.cta_title}</h2>
          <p className="text-white/60 text-lg mb-10">{t.home.cta_text}</p>
          <Link to="/contact" data-testid="home-cta-book" className="bg-[#C8A75A] text-black hover:bg-white transition-colors duration-300 font-medium px-10 py-5 rounded-md text-sm uppercase tracking-widest inline-flex items-center gap-2">
            {t.home.cta_book} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
